/**
 * HistoryTab component
 *
 * Activity timeline for a security issue, powered by the
 * issue_activities API endpoint. Each entry shows the activity type,
 * context-specific details extracted from the JSONB payload, the actor,
 * and a relative timestamp.
 */

import { useState } from "react";
import { Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useIssueActivities } from "@/hooks/useIssueActivities";
import { formatTimeAgo } from "@/utils/securityHelpers";
import { ResponseSummaryStrip } from "./ResponseSummaryStrip";
import type { IssueActivity } from "@/types/issueActivity";
import type { SecurityIssue } from "@/types/security";

interface HistoryTabProps {
  issue: SecurityIssue;
  numericIssueId: number;
}

const ACTIVITY_LABELS: Record<string, string> = {
  ISSUE_CREATED: "Issue created",
  STATUS_CHANGED: "Status changed",
  SEVERITY_CHANGED: "Severity changed",
  ASSIGNED: "Assigned",
  UNASSIGNED: "Unassigned",
  COMMENT_ADDED: "Comment added",
  COMMENT_EDITED: "Comment edited",
  ACTION_EXECUTED: "Action executed",
  TICKET_CREATED: "Ticket created",
  SUPPRESSION_CREATED: "Suppression rule created",
  EXPORTED: "Exported",
  TRACE_LINKED: "Trace linked",
  INTEGRATION_CONFIGURED: "Integration configured",
  INTEGRATION_UPDATED: "Integration updated",
  INTEGRATION_REMOVED: "Integration removed",
};

/** Render context-specific inline details from the JSONB `details` field. */
function ActivityDetail({ activity }: { activity: IssueActivity }) {
  const d = activity.details;
  if (!d) return null;

  switch (activity.activity_type) {
    case "STATUS_CHANGED":
    case "SEVERITY_CHANGED":
      return (
        <span className="inline-flex items-center gap-1">
          <Badge variant="outline" className="text-[10px] px-1.5 py-0 font-mono">
            {String(d.from)}
          </Badge>
          <span className="text-muted-foreground text-xs">&rarr;</span>
          <Badge variant="outline" className="text-[10px] px-1.5 py-0 font-mono">
            {String(d.to)}
          </Badge>
        </span>
      );

    case "ASSIGNED":
      return (
        <span className="text-xs text-muted-foreground">
          to {String(d.assigned_to_name ?? "unknown")}
        </span>
      );

    case "UNASSIGNED":
      return (
        <span className="text-xs text-muted-foreground">
          from {String(d.unassigned_from_name ?? "unknown")}
        </span>
      );

    case "COMMENT_ADDED":
    case "COMMENT_EDITED":
      return d.preview ? (
        <span className="text-xs text-muted-foreground truncate max-w-[360px] inline-block align-bottom">
          &ldquo;{String(d.preview)}&rdquo;
        </span>
      ) : null;

    case "ACTION_EXECUTED":
      return d.action_type ? (
        <Badge variant="secondary" className="text-[10px] px-1.5 py-0 font-mono">
          {String(d.action_type)}
        </Badge>
      ) : null;

    case "TICKET_CREATED":
      return (
        <span className="inline-flex items-center gap-1.5">
          {d.ticket_key != null && (
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0 font-mono">
              {String(d.ticket_key)}
            </Badge>
          )}
          {d.provider != null && (
            <span className="text-xs text-muted-foreground capitalize">
              {String(d.provider)}
            </span>
          )}
        </span>
      );

    case "TRACE_LINKED":
      return d.trace_id ? (
        <span className="text-xs text-muted-foreground font-mono">
          #{String(d.trace_id)}
        </span>
      ) : null;

    case "SUPPRESSION_CREATED":
      return d.rule_type ? (
        <span className="text-xs text-muted-foreground">{String(d.rule_type)}</span>
      ) : null;

    default:
      return null;
  }
}

function formatTimestamp(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function HistoryTab({ issue, numericIssueId }: HistoryTabProps) {
  const [page, setPage] = useState(1);
  const { data, isLoading, isError } = useIssueActivities(numericIssueId, page);

  const activities = data?.data ?? [];
  const hasMore = data?.has_more ?? false;

  // The strip fetches its own page-1 data so metrics stay stable
  // regardless of which timeline page the user is viewing.
  const strip = (
    <ResponseSummaryStrip issue={issue} numericIssueId={numericIssueId} />
  );

  if (isLoading && activities.length === 0) {
    return (
      <div className="mt-6 space-y-5">
        {strip}
        <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin mr-2" />
          Loading activity&hellip;
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="mt-6 space-y-5">
        {strip}
        <div className="py-16 text-center text-sm text-muted-foreground">
          Failed to load activity history.
        </div>
      </div>
    );
  }

  if (!isLoading && activities.length === 0) {
    return (
      <div className="mt-6 space-y-5">
        {strip}
        <div className="py-16 text-center text-sm text-muted-foreground">
          No activity recorded yet.
        </div>
      </div>
    );
  }

  return (
    <div className="mt-6 space-y-5">
      {strip}

      {/* Timeline */}
      <div>
        <h3 className="text-sm font-semibold text-foreground mb-4">
          Activity Timeline
        </h3>

        <div className="relative pl-5">
          {/* Vertical connector */}
          <div className="absolute left-[5px] top-1 bottom-1 w-px bg-border" />

          {activities.map((activity, idx) => (
            <div
              key={activity.id}
              className="relative flex gap-4 py-3 first:pt-0 last:pb-0"
            >
              {/* Dot */}
              <div className="absolute left-[-20px] top-[14px] z-10">
                <div
                  className={`h-[9px] w-[9px] rounded-full ring-2 ring-background ${
                    idx === 0 && page === 1
                      ? "bg-foreground"
                      : "bg-muted-foreground/30"
                  }`}
                />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0 flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-foreground">
                      {ACTIVITY_LABELS[activity.activity_type] ?? activity.activity_type}
                    </span>
                    <ActivityDetail activity={activity} />
                  </div>
                  {activity.user_name && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      by {activity.user_name}
                    </p>
                  )}
                </div>

                <time
                  className="text-xs text-muted-foreground whitespace-nowrap flex-shrink-0 pt-0.5"
                  dateTime={activity.created_at}
                  title={formatTimestamp(activity.created_at)}
                >
                  {formatTimeAgo(activity.created_at)}
                </time>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {(page > 1 || hasMore) && (
          <div className="mt-6 flex items-center justify-between border-t pt-3">
            <Button
              variant="ghost"
              size="sm"
              className="text-xs gap-1"
              onClick={() => setPage((p) => p - 1)}
              disabled={page <= 1 || isLoading}
            >
              <ChevronLeft className="h-3 w-3" />
              Newer
            </Button>

            <span className="text-xs text-muted-foreground">Page {page}</span>

            <Button
              variant="ghost"
              size="sm"
              className="text-xs gap-1"
              onClick={() => setPage((p) => p + 1)}
              disabled={!hasMore || isLoading}
            >
              Older
              <ChevronRight className="h-3 w-3" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
